"use client"

import { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { 
  ArrowLeft, 
  Building2, 
  Mail, 
  Phone, 
  MapPin, 
  FileText, 
  Check, 
  X, 
  Calendar,
  Loader2,
  Edit,
  ExternalLink
} from "lucide-react"
import { Badge } from "@/components/ui/badge"
import SupplierService from "@/api/suppliers.service"
import { useToast } from "@/hooks/use-toast"

interface SupplierDetailProps {
  publicId?: string
}

export default function SupplierDetail({ publicId }: SupplierDetailProps) {
  const params = useParams<{ publicId: string }>();
  const id = publicId || params.publicId;
  const [supplier, setSupplier] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchSupplier = async () => {
      setIsLoading(true);
      try {
        const response = await SupplierService.getSupplier(id as string);
        if (response.data.result === "success") {
          setSupplier(response.data.supplier);
        } else {
          setError(response.data.message || "Failed to fetch supplier details");
          toast({
            title: "Error",
            description: response.data.message || "Failed to fetch supplier details",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error(error);
        setError("An error occurred while fetching supplier details");
        toast({
          title: "Error",
          description: "An error occurred while fetching supplier details",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchSupplier();
    }
  }, [id, toast]);

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        <div className="flex justify-center items-center h-64">
          <div className="flex flex-col items-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mb-4" />
            <p className="text-lg text-slate-600">Loading supplier details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !supplier) {
    return (
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-semibold text-slate-900">Supplier Details</h1>
          <Button 
            onClick={() => window.history.back()} 
            variant="outline" 
            className="border-slate-200 text-slate-700 hover:bg-slate-100"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
        
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex flex-col items-center justify-center h-40">
              <X className="h-10 w-10 text-rose-500 mb-4" />
              <p className="text-lg font-medium text-slate-900 mb-1">Supplier not found</p>
              <p className="text-slate-500">{error || "Unable to load supplier details"}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  };

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <div className="flex flex-col space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">{supplier.name}</h1>
            <p className="text-slate-500 mt-1">Supplier Details</p>
          </div>
          <div className="flex space-x-3">
            <Button 
              onClick={() => window.history.back()} 
              variant="outline" 
              className="border-slate-200 text-slate-700 hover:bg-slate-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Button 
              onClick={() => window.location.href = `/supplier/edit/${supplier.publicId}`} 
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Edit className="w-4 h-4 mr-2" /> Edit Supplier
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Information Card */}
          <Card className="border border-slate-200 shadow-sm md:col-span-2">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-medium flex items-center gap-2">
                <Building2 className="h-5 w-5 text-blue-600" />
                Supplier Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-start">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <Building2 className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-slate-500">Company Name</p>
                  <p className="text-lg font-semibold text-slate-900">{supplier.name}</p>
                </div>
              </div>

              <Separator className="my-6 bg-slate-200" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {supplier.contactPerson && (
                  <div className="flex items-start">
                    <div className="mt-1">
                      <p className="text-sm font-medium text-slate-500">Contact Person</p>
                      <p className="text-slate-900">{supplier.contactPerson}</p>
                    </div>
                  </div>
                )}

                {supplier.email && (
                  <div className="flex items-start">
                    <div className="mt-1">
                      <p className="text-sm font-medium text-slate-500">Email Address</p>
                      <a 
                        href={`mailto:${supplier.email}`} 
                        className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                      >
                        <Mail className="h-4 w-4" /> {supplier.email}
                      </a>
                    </div>
                  </div>
                )}

                {supplier.phoneNumber && (
                  <div className="flex items-start">
                    <div className="mt-1">
                      <p className="text-sm font-medium text-slate-500">Phone Number</p>
                      <a 
                        href={`tel:${supplier.phoneNumber}`} 
                        className="text-slate-900 hover:text-slate-700 flex items-center gap-1"
                      >
                        <Phone className="h-4 w-4" /> {supplier.phoneNumber}
                      </a>
                    </div>
                  </div>
                )}

                {supplier.taxId && (
                  <div className="flex items-start">
                    <div className="mt-1">
                      <p className="text-sm font-medium text-slate-500">Tax ID</p>
                      <p className="text-slate-900 flex items-center gap-1">
                        <FileText className="h-4 w-4" /> {supplier.taxId}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {supplier.address && (
                <>
                  <Separator className="my-6 bg-slate-200" />
                  
                  <div>
                    <p className="text-sm font-medium text-slate-500 mb-2">Address</p>
                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                      <div className="flex">
                        <MapPin className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
                        <div className="ml-3">
                          <p className="text-slate-900">{supplier.address}</p>
                          <p className="text-slate-700 mt-1">
                            {[
                              supplier.city, 
                              supplier.state, 
                              supplier.postalCode, 
                              supplier.country
                            ].filter(Boolean).join(', ')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {supplier.notes && (
                <>
                  <Separator className="my-6 bg-slate-200" />
                  
                  <div>
                    <p className="text-sm font-medium text-slate-500 mb-2">Notes</p>
                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                      <p className="text-slate-700 whitespace-pre-line">{supplier.notes}</p>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Status and Details Card */}
          <div className="space-y-6">
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-medium">Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-slate-700 font-medium">Current Status</p>
                  <Badge 
                    variant="outline" 
                    className={`font-medium px-2.5 py-1 rounded-full ${
                      supplier.status === 'active'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-amber-50 text-amber-700 border-amber-200'
                    }`}
                  >
                    {supplier.status === 'active' ? (
                      <Check className="h-3.5 w-3.5 mr-1.5" />
                    ) : (
                      <X className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    <span className="capitalize">{supplier.status}</span>
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-medium">Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <p className="text-slate-500">Created On</p>
                  <p className="text-slate-900 font-medium flex items-center">
                    <Calendar className="h-4 w-4 mr-1.5 text-slate-400" />
                    {formatDate(supplier.dateCreated)}
                  </p>
                </div>
                
                {supplier.dateUpdated && (
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <p className="text-slate-500">Last Updated</p>
                    <p className="text-slate-900 font-medium flex items-center">
                      <Calendar className="h-4 w-4 mr-1.5 text-slate-400" />
                      {formatDate(supplier.dateUpdated)}
                    </p>
                  </div>
                )}
                
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <p className="text-slate-500">Public ID</p>
                  <p className="text-slate-900 font-medium">{supplier.publicId.slice(0, 8)}</p>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions Card */}
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-medium">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start border-slate-200 text-slate-700 hover:bg-slate-100"
                  onClick={() => window.location.href = `/supplier/edit/${supplier.publicId}`}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Supplier
                </Button>
                
                {supplier.email && (
                  <Button 
                    variant="outline" 
                    className="w-full justify-start border-slate-200 text-slate-700 hover:bg-slate-100"
                    onClick={() => window.location.href = `mailto:${supplier.email}`}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Send Email
                  </Button>
                )}

                {/* Link to create bills with this supplier */}
                <Button 
                  variant="outline" 
                  className="w-full justify-start border-blue-200 text-blue-700 hover:bg-blue-50"
                  onClick={() => window.location.href = `/bills/create?supplier=${supplier.publicId}`}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Create Bill
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
} 